export const ROBOTICS_DAY_COLORS = [
  'linear-gradient(135deg, #0EA5E9, #10B981)',
  'linear-gradient(135deg, #10B981, #06B6D4)',
  'linear-gradient(135deg, #06B6D4, #3B82F6)',
  'linear-gradient(135deg, #3B82F6, #8B5CF6)',
  'linear-gradient(135deg, #8B5CF6, #EC4899)',
];

export const ROBOTICS_DAYS = [
  // ─────────────────────────────────────────────────────────────
  // DAY 1 — MONDAY: Intro to Robotics & Coding
  // ─────────────────────────────────────────────────────────────
  {
    day: 1,
    weekday: { en: 'Monday', ja: '月曜日' },
    theme: { en: 'Welcome to Robotics! 🤖 What is Code?', ja: 'ロボティクスへようこそ！🤖 コードって何？' },
    goal: {
      en: 'Understand what robots and code are, set up Trinket accounts, and run your very first Python program!',
      ja: 'ロボットとコードが何かを理解し、Trinketアカウントをセットアップして、初めてのPythonプログラムを実行しよう！',
    },
    vocab: [
      { word: { en: 'code', ja: 'コード' }, def: { en: 'Instructions written in a language the computer understands', ja: 'コンピューターが理解できる言語で書かれた命令' }, emoji: '💻' },
      { word: { en: 'program', ja: 'プログラム' }, def: { en: 'A set of code instructions that tells the computer what to do', ja: 'コンピューターに何をすべきかを伝えるコードの集まり' }, emoji: '📋' },
      { word: { en: 'Python', ja: 'Python' }, def: { en: 'A beginner-friendly coding language used for robots, games, and art', ja: 'ロボット・ゲーム・アートに使われる初心者向けのプログラミング言語' }, emoji: '🐍' },
      { word: { en: 'run', ja: '実行する' }, def: { en: 'Tell the computer to execute your code', ja: 'コンピューターにコードを実行させる' }, emoji: '▶️' },
      { word: { en: 'robot', ja: 'ロボット' }, def: { en: 'A machine controlled by code to do tasks automatically', ja: 'コードによって制御されてタスクを自動的に行う機械' }, emoji: '🤖' },
    ],
    morning: { time: { en: '', ja: '' }, activities: [] },
    afternoon: {
      time: { en: '2:00 – 4:00 PM', ja: '午後2:00 – 4:00' },
      activities: [
        {
          time: '2:00',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🤖 What is a Robot? — Class Discussion', ja: '🤖 ロボットって何？ — クラスディスカッション' },
          type: 'lesson',
          emoji: '🤖',
          content: {
            en: 'Teacher asks: "What robots do you know?" Students share examples. Key message: ALL robots are controlled by code. Show a short clip of robots in action.',
            ja: '先生が質問：「知っているロボットは？」生徒がシェア。重要メッセージ：全てのロボットはコードで制御されている。ロボットの動画を短く見る。',
          },
        },
        {
          time: '2:15',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '📚 Flashcards — Key Words', ja: '📚 フラッシュカード — 重要単語' },
          type: 'lesson',
          emoji: '📚',
          content: {
            en: 'Go through today\'s 5 vocab cards: code · program · Python · run · robot',
            ja: '今日の5つの単語カードを確認：code · program · Python · run · robot',
          },
        },
        {
          time: '2:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: '🛠️ Setup — Create Your Trinket Account', ja: '🛠️ セットアップ — Trinketアカウントを作ろう' },
          type: 'activity',
          emoji: '🛠️',
          content: {
            en: 'Everyone opens trinket.io. Teacher walks through account setup. Once logged in: New Trinket → Python. Teacher shows: code editor (left), result window (right), Run button (▶).',
            ja: '全員がtrinket.ioを開く。先生がアカウント設定を説明。ログイン後：New Trinket → Python。コードエディター（左）、結果（右）、実行ボタン（▶）。',
          },
        },
        {
          time: '2:45',
          duration: { en: '45 min', ja: '45分' },
          title: { en: '💻 First Program — Hello, Robot!', ja: '💻 初めてのプログラム — Hello, Robot!' },
          type: 'activity',
          emoji: '🐍',
          content: {
            en: 'Type and run:\n\nprint("Hello, Robot!")\nprint("My name is ___")\nprint("I am a programmer!")\n\nThen try:\n✏️ Change the message to your name\n✏️ Add a funny 4th line\n✏️ Try: print("🤖" * 10)\n✏️ Try: print(2 + 2)',
            ja: '入力して実行：\nprint("Hello, Robot!")\nprint("名前は___です")\nprint("私はプログラマーです！")\n\nそれから試す：\n✏️ 名前に変える\n✏️ 面白い4行目を追加\n✏️ print("🤖" * 10)\n✏️ print(2 + 2)',
          },
        },
        {
          time: '3:30',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🎉 Share — What Did Your Computer Say?', ja: '🎉 シェア — コンピューターは何を言った？' },
          type: 'share',
          emoji: '⭐',
          content: {
            en: '3 students share their screen. Show what you printed and explain one thing you changed.',
            ja: '3人の生徒が画面をシェア。印刷した内容を見せて、変えたことを1つ説明する。',
          },
        },
        {
          time: '3:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🌟 Wrap-Up & Stamp', ja: '🌟 まとめ＆スタンプ' },
          type: 'stamp',
          emoji: '🏅',
          content: {
            en: '"Today I learned that robots need ___ to work. I ran my first program!"\n\n⭐ Stamp criteria: Ran at least 3 print statements AND changed the message to make it their own.',
            ja: '「今日ロボットは___が必要だと学んだ。初めてのプログラムを実行した！」\n\n⭐ スタンプ条件：3つ以上のprint文を実行し、メッセージを自分のものに変えた。',
          },
        },
      ],
    },
    worksheets: [],
  },

  // ─────────────────────────────────────────────────────────────
  // DAY 2 — TUESDAY: Trinket.io – Shapes & Colors
  // ─────────────────────────────────────────────────────────────
  {
    day: 2,
    weekday: { en: 'Tuesday', ja: '火曜日' },
    theme: { en: 'Drawing with Python 🐢 Shapes & Colors', ja: 'Pythonで絵を描こう 🐢 図形と色' },
    goal: {
      en: 'Write Python code that draws colorful shapes — run it and watch the turtle draw live!',
      ja: 'Pythonコードでカラフルな図形を描こう — 実行するとタートルがリアルタイムで描く！',
    },
    vocab: [
      { word: { en: 'import', ja: 'import' }, def: { en: 'Load a tool/library so you can use it', ja: 'ツールやライブラリを読み込んで使えるようにする' }, emoji: '📦' },
      { word: { en: 'turtle', ja: 'turtle' }, def: { en: 'A Python drawing tool that moves like a pen', ja: 'ペンのように動くPythonの描画ツール' }, emoji: '🐢' },
      { word: { en: 'forward()', ja: 'forward()' }, def: { en: 'Move the turtle forward by some steps', ja: 'タートルを指定のステップ数だけ前に動かす' }, emoji: '➡️' },
      { word: { en: 'right() / left()', ja: 'right() / left()' }, def: { en: 'Turn the turtle right or left by degrees', ja: 'タートルを右または左に角度分だけ回転させる' }, emoji: '↩️' },
      { word: { en: 'for loop', ja: 'forループ' }, def: { en: 'Repeat code a set number of times', ja: 'コードを指定回数繰り返す' }, emoji: '🔄' },
      { word: { en: 'color()', ja: 'color()' }, def: { en: 'Set the pen color before drawing', ja: '描く前にペンの色を設定する' }, emoji: '🎨' },
    ],
    morning: { time: { en: '', ja: '' }, activities: [] },
    afternoon: {
      time: { en: '2:00 – 4:00 PM', ja: '午後2:00 – 4:00' },
      activities: [
        {
          time: '2:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '📚 Flashcards — Key Python Words', ja: '📚 フラッシュカード — 重要なPython単語' },
          type: 'lesson',
          emoji: '📚',
          content: {
            en: 'Go through today\'s 6 vocab cards as a class. For each card: teacher shows the word, students guess the meaning, then flip to see definition.\n\nWords: import · turtle · forward() · right()/left() · for loop · color()',
            ja: 'クラスで今日の6つの単語カードを確認する。各カード：先生が単語を見せて、生徒が意味を推測し、カードをめくって定義を確認。\n\n単語: import · turtle · forward() · right()/left() · forループ · color()',
          },
        },
        {
          time: '2:10',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🖥️ Teacher Demo — Run the Starter Code', ja: '🖥️ 先生デモ — スターターコードを実行しよう' },
          type: 'lesson',
          emoji: '🎓',
          content: {
            en: 'Teacher opens trinket.io on the projector and runs this code live:\n\nimport turtle\nt = turtle.Turtle()\nt.color("red")\nfor i in range(4):\n    t.forward(100)\n    t.right(90)\n\nAsk students: "What shape will this draw?" Run it — a red square appears!\n\nThen change: color("blue"), range(3) → triangle. Show that changing ONE number changes everything.',
            ja: '先生がプロジェクターでtrinket.ioを開き、このコードをライブで実行。\n生徒に質問：「どんな形を描くと思う？」実行すると — 赤い正方形が現れる！\nそれから変える：color("blue")、range(3) → 三角形。1つの数字を変えると全てが変わる。',
          },
        },
        {
          time: '2:25',
          duration: { en: '60 min', ja: '60分' },
          title: { en: '💻 Hands-On Coding — Shapes & Colors', ja: '💻 ハンズオンコーディング — 図形と色' },
          type: 'activity',
          emoji: '🐢',
          content: {
            en: 'Students open trinket.io and type the starter code. Then modify:\n\n✏️ Challenge 1: Change the color to your favorite color\n✏️ Challenge 2: Change range(4) to range(3) — what happens?\n✏️ Challenge 3: Change forward(100) to forward(150) — bigger square!\n✏️ Challenge 4: Draw a circle → t.circle(50)\n✏️ Challenge 5: Draw 3 different shapes in 3 different colors\n\nStarter code:\n\nimport turtle\nt = turtle.Turtle()\nt.speed(3)\nt.color("red")\nt.begin_fill()\nfor i in range(4):\n    t.forward(100)\n    t.right(90)\nt.end_fill()',
            ja: '生徒がtrinket.ioを開いてスターターコードを入力。それから変更：\n\n✏️ チャレンジ1：色をお気に入りの色に変える\n✏️ チャレンジ2：range(4)をrange(3)に変える\n✏️ チャレンジ3：forward(100)をforward(150)に変える\n✏️ チャレンジ4：円を描く → t.circle(50)\n✏️ チャレンジ5：3つの異なる色で3つの形を描く',
          },
        },
        {
          time: '3:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: '🎉 Showcase — Show Your Drawing!', ja: '🎉 発表 — 描いた絵を見せよう！' },
          type: 'share',
          emoji: '⭐',
          content: {
            en: '2–3 students share their screen and show their drawing. Explain: "I changed ___ and it made ___." Class applauds each one.\n\nTeacher asks: "What would happen if we used 36 sides instead of 4?" → hint at tomorrow\'s spiral!',
            ja: '2〜3人の生徒が画面を共有して絵を見せる。説明：「___を変えたら___になった。」\n先生が質問：「4つの代わりに36の辺を使ったらどうなる？」→ 明日のスパイラルのヒント！',
          },
        },
        {
          time: '3:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🌟 Wrap-Up & Stamp', ja: '🌟 まとめ＆スタンプ' },
          type: 'stamp',
          emoji: '🏅',
          content: {
            en: 'Each student answers: "Today I changed ___ and it made the turtle ___. Tomorrow I want to try ___!"\n\n⭐ Stamp criteria: Ran the starter code AND changed at least 2 things to make it their own.',
            ja: '各生徒が答える：「今日は___を変えてタートルが___になった。明日は___を試したい！」\n\n⭐ スタンプ条件：スターターコードを実行し、2つ以上変更した。',
          },
        },
      ],
    },
    worksheets: [],
  },

  // ─────────────────────────────────────────────────────────────
  // DAY 3 — WEDNESDAY: Trinket.io – Spiral Art
  // ─────────────────────────────────────────────────────────────
  {
    day: 3,
    weekday: { en: 'Wednesday', ja: '水曜日' },
    theme: { en: 'Spiral Art with Python 🌀 Patterns & Color Cycles', ja: 'Pythonでスパイラルアート 🌀 パターンと色のサイクル' },
    goal: {
      en: 'Create a beautiful spiral by looping 200 times — change the angle and colors to make it totally your own!',
      ja: '200回ループして美しいスパイラルを作ろう — 角度と色を変えて完全に自分だけのものにしよう！',
    },
    vocab: [
      { word: { en: 'list', ja: 'リスト' }, def: { en: 'A collection of items in order: ["red", "blue", "green"]', ja: '順序付きアイテムの集合：["red", "blue", "green"]' }, emoji: '📝' },
      { word: { en: 'i % 6', ja: 'i % 6' }, def: { en: 'Remainder when dividing by 6 — cycles through the color list', ja: '6で割った余り — 色リストを繰り返す' }, emoji: '🔁' },
      { word: { en: 'speed(0)', ja: 'speed(0)' }, def: { en: 'Draw as fast as possible', ja: 'できるだけ速く描く' }, emoji: '⚡' },
      { word: { en: 'i * 0.5', ja: 'i * 0.5' }, def: { en: 'Step grows each loop — creates the spiral effect', ja: 'ループごとにステップが大きくなる — スパイラル効果' }, emoji: '📈' },
      { word: { en: 'angle', ja: '角度' }, def: { en: 'Degrees to turn — small changes make totally different patterns', ja: '回転角度 — 少しの変化で全く異なるパターンになる' }, emoji: '📐' },
    ],
    morning: { time: { en: '', ja: '' }, activities: [] },
    afternoon: {
      time: { en: '2:00 – 4:00 PM', ja: '午後2:00 – 4:00' },
      activities: [
        {
          time: '2:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '📚 Flashcards — Today\'s Key Words', ja: '📚 フラッシュカード — 今日の重要単語' },
          type: 'lesson',
          emoji: '📚',
          content: {
            en: 'Review today\'s 5 vocab cards. Focus on: list, modulo (%), speed(0), angle.\n\nQuick review from Day 1: "What does forward() do? What does a for loop do?"',
            ja: '今日の5つの単語カードを確認。重点：list、余り(%)、speed(0)、角度。\n\nDay 1の復習：「forward()は何をする？forループは何をする？」',
          },
        },
        {
          time: '2:10',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🖥️ Teacher Demo — The Magic Spiral', ja: '🖥️ 先生デモ — 魔法のスパイラル' },
          type: 'lesson',
          emoji: '🌀',
          content: {
            en: 'Teacher runs this code on the projector:\n\nimport turtle\nt = turtle.Turtle()\nt.speed(0)\ncolors = ["red", "orange", "yellow", "green", "blue", "purple"]\nfor i in range(200):\n    t.color(colors[i % 6])\n    t.forward(i * 0.5)\n    t.left(91)\n\n200 lines in seconds! Ask: "What if I change 91 to 90? To 89? To 120?"\nRun 3 times with different angles — completely different patterns each time!',
            ja: '先生がプロジェクターでコードを実行。200本の線が数秒で描かれる！\n質問：「91を90に変えたらどうなる？89に？120に？」\n異なる角度で3回実行 — 毎回全く異なるパターン！',
          },
        },
        {
          time: '2:25',
          duration: { en: '60 min', ja: '60分' },
          title: { en: '💻 Create Your Signature Spiral Art', ja: '💻 自分だけのスパイラルアートを作ろう' },
          type: 'activity',
          emoji: '🎨',
          content: {
            en: 'Copy the starter code and make it your own:\n\n✏️ Change the colors list — use your favorite colors\n✏️ Change the angle (try 90, 91, 89, 120, 137)\n✏️ Change range(200) to range(300) — bigger spiral\n✏️ Change i * 0.5 to i * 0.3 or i * 1.0\n✏️ Change left() to right()\n\nStarter code:\n\nimport turtle\nt = turtle.Turtle()\nt.speed(0)\ncolors = ["red", "orange", "yellow", "green", "blue", "purple"]\nfor i in range(200):\n    t.color(colors[i % 6])\n    t.forward(i * 0.5)\n    t.left(91)',
            ja: 'スターターコードをコピーして自分のものにする：\n\n✏️ colorsリストをお気に入りの色に変える\n✏️ 角度を変える（90、91、89、120、137を試す）\n✏️ range(200)をrange(300)に変える\n✏️ i * 0.5をi * 0.3またはi * 1.0に変える\n✏️ left()をright()に変える',
          },
        },
        {
          time: '3:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: '🎨 Art Gallery — Show Your Spiral!', ja: '🎨 アートギャラリー — スパイラルを見せよう！' },
          type: 'share',
          emoji: '🖼️',
          content: {
            en: 'Everyone shows their spiral for 30 seconds. Say: "I changed the angle to ___ and the colors to ___ and this is what happened!"\n\nClass votes: 🌈 Most Colorful · 🌀 Most Hypnotic · 🎯 Most Unique\n\nPreview: "Next 2 days we control REAL robots!"',
            ja: '全員が30秒間スパイラルを表示。「角度を___に、色を___に変えたらこうなった！」\n\nクラスが投票：🌈 最もカラフル · 🌀 最も催眠的 · 🎯 最もユニーク\n\nプレビュー：「次の2日間は本物のロボットを制御する！」',
          },
        },
        {
          time: '3:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🌟 Wrap-Up & Stamp', ja: '🌟 まとめ＆スタンプ' },
          type: 'stamp',
          emoji: '🏅',
          content: {
            en: '"My spiral used angle ___ and I think it looks like ___!"\n\n⭐ Stamp criteria: Created a spiral visibly different from the starter — changed at least angle + one other value.',
            ja: '「私のスパイラルは角度___を使って___みたいに見える！」\n\n⭐ スタンプ条件：スターターと明らかに異なるスパイラルを作った。',
          },
        },
      ],
    },
    worksheets: [],
  },

  // ─────────────────────────────────────────────────────────────
  // DAY 4 — THURSDAY: Browserbotics – Project 1
  // ─────────────────────────────────────────────────────────────
  {
    day: 4,
    weekday: { en: 'Thursday', ja: '木曜日' },
    theme: { en: 'Real Robots! 🦾 Browserbotics Project 1', ja: '本物のロボット！🦾 Browserbotics プロジェクト1' },
    goal: {
      en: 'Run Python code that controls a real robot simulation — choose your first robot project and make it move!',
      ja: 'Pythonコードで本物のロボットシミュレーションを制御しよう — 最初のロボットプロジェクトを選んで動かそう！',
    },
    vocab: [
      { word: { en: 'robot', ja: 'ロボット' }, def: { en: 'A machine we control with code', ja: 'コードで制御する機械' }, emoji: '🤖' },
      { word: { en: 'joint', ja: 'ジョイント' }, def: { en: 'A moving part of the robot arm (like a shoulder or elbow)', ja: 'ロボットアームの動く部分（肩や肘のような）' }, emoji: '🦾' },
      { word: { en: 'targetPosition', ja: 'targetPosition' }, def: { en: 'The angle we want a joint to move to', ja: 'ジョイントを動かしたい角度' }, emoji: '🎯' },
      { word: { en: 'time.sleep()', ja: 'time.sleep()' }, def: { en: 'Pause the code for a number of seconds', ja: 'コードを数秒間一時停止する' }, emoji: '⏱️' },
      { word: { en: 'def', ja: 'def' }, def: { en: 'Create a function — a reusable block of code with a name', ja: '関数を作る — 名前のついた再利用できるコードのブロック' }, emoji: '⚙️' },
    ],
    morning: { time: { en: '', ja: '' }, activities: [] },
    afternoon: {
      time: { en: '2:00 – 4:00 PM', ja: '午後2:00 – 4:00' },
      activities: [
        {
          time: '2:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '📚 Flashcards — Robot Vocabulary', ja: '📚 フラッシュカード — ロボット単語' },
          type: 'lesson',
          emoji: '📚',
          content: {
            en: 'Go through today\'s robot vocab cards. Key question for each: "Where do you think we use this in the robot code?"\n\nWords: robot · joint · targetPosition · time.sleep() · def',
            ja: '今日のロボット単語カードを確認。各カードの質問：「このロボットコードのどこでこれを使うと思う？」\n\n単語: robot · joint · targetPosition · time.sleep() · def',
          },
        },
        {
          time: '2:10',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🖥️ Teacher Demo — The Robot Comes Alive!', ja: '🖥️ 先生デモ — ロボットが動き出す！' },
          type: 'lesson',
          emoji: '🤖',
          content: {
            en: 'Teacher opens Browserbotics and runs the gripper code. Walk through key parts:\n\n1. bb.loadURDF("viporX300.urdf") — loads the robot\n2. bb.setJointMotorControl(...) — moves a joint\n3. time.sleep(4) — wait to see movement\n4. open_gripper() / close_gripper() — calling functions\n\nRun it live. Then change targetPosition values to show what happens.',
            ja: '先生がBrowserboticsを開いてグリッパーコードを実行。重要な部分を説明：\n\n1. bb.loadURDF("viporX300.urdf") — ロボットを読み込む\n2. bb.setJointMotorControl(...) — ジョイントを動かす\n3. time.sleep(4) — 動きを見るために待つ\n4. open_gripper() / close_gripper() — 関数を呼び出す',
          },
        },
        {
          time: '2:25',
          duration: { en: '60 min', ja: '60分' },
          title: { en: '💻 Pick Your Project & Make the Robot Move!', ja: '💻 プロジェクトを選んでロボットを動かそう！' },
          type: 'activity',
          emoji: '🦾',
          content: {
            en: 'Each student picks ONE project for today:\n\n🤏 PROJECT A — Gripper: Open & Close\nCopy starter code → run it → change targetPosition values → make gripper open wider or close tighter → change time.sleep to speed up or slow down.\n\n💪 PROJECT B — Robot Arm Movement\nLoad the robot, change arm_position array values one at a time, watch which joint each number controls.\n\n✏️ Modify challenges (both):\n• Change how many times it opens/closes\n• Change the speed (time.sleep values)\n• Change position values and predict the result before running',
            ja: '各生徒が今日の1つのプロジェクトを選ぶ：\n\n🤏 プロジェクトA — グリッパー：開閉\nスターターコードをコピー → 実行 → targetPositionの値を変える → speed/回数を変える。\n\n💪 プロジェクトB — ロボットアームの動き\narm_positionの値を1つずつ変えて、各番号がどのジョイントを制御するか見る。\n\n✏️ 変更チャレンジ：速度、回数、ポジションを変えて予測してから実行！',
          },
        },
        {
          time: '3:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: '🎉 Show Your Robot!', ja: '🎉 ロボットを見せよう！' },
          type: 'share',
          emoji: '🤖',
          content: {
            en: '2–3 students show their robot running. Explain: "I chose project ___ and I changed ___ which made the robot ___."',
            ja: '2〜3人の生徒がロボットを画面で実行して見せる。「プロジェクト___を選んで、___を変えたらロボットが___になった。」',
          },
        },
        {
          time: '3:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🌟 Wrap-Up & Stamp', ja: '🌟 まとめ＆スタンプ' },
          type: 'stamp',
          emoji: '🏅',
          content: {
            en: '"Today I controlled a robot by changing ___. Tomorrow I will try project ___!"\n\n⭐ Stamp criteria: Successfully ran robot code AND changed at least 2 values to see a different result.',
            ja: '「今日は___を変えてロボットを制御した。明日はプロジェクト___を試す！」\n\n⭐ スタンプ条件：ロボットコードを実行し、2つ以上の値を変えた。',
          },
        },
      ],
    },
    worksheets: [],
  },

  // ─────────────────────────────────────────────────────────────
  // DAY 5 — FRIDAY: Browserbotics – Project 2
  // ─────────────────────────────────────────────────────────────
  {
    day: 5,
    weekday: { en: 'Friday', ja: '金曜日' },
    theme: { en: 'Robot Challenge Day! 🚀 Browserbotics Project 2', ja: 'ロボットチャレンジデー！🚀 Browserbotics プロジェクト2' },
    goal: {
      en: 'Try a new robot project — then combine what you learned from both days to create your best robot program!',
      ja: '新しいロボットプロジェクトに挑戦 — 2日間で学んだことを組み合わせて最高のロボットプログラムを作ろう！',
    },
    vocab: [
      { word: { en: 'parameter', ja: 'パラメーター' }, def: { en: 'A value you pass into a function to control what it does', ja: '関数に渡して動作を制御する値' }, emoji: '🎛️' },
      { word: { en: 'simulation', ja: 'シミュレーション' }, def: { en: 'A computer model that acts like the real thing', ja: '本物のように動くコンピューターモデル' }, emoji: '💻' },
      { word: { en: 'robot dog', ja: 'ロボット犬' }, def: { en: 'A four-legged robot that walks like an animal', ja: '動物のように歩く4本脚のロボット' }, emoji: '🐕' },
    ],
    morning: { time: { en: '', ja: '' }, activities: [] },
    afternoon: {
      time: { en: '2:00 – 4:00 PM', ja: '午後2:00 – 4:00' },
      activities: [
        {
          time: '2:00',
          duration: { en: '10 min', ja: '10分' },
          title: { en: '📚 Flashcards — Quick Review + New Words', ja: '📚 フラッシュカード — 復習＋新しい単語' },
          type: 'lesson',
          emoji: '📚',
          content: {
            en: 'Quick review of yesterday\'s words, then add today\'s new ones: parameter · simulation · robot dog\n\nAsk: "Yesterday you ran project A or B — what was the most interesting thing you changed?"',
            ja: '昨日の単語を素早く復習してから今日の新しい単語を追加：parameter · simulation · robot dog\n\n質問：「昨日プロジェクトAかBを実行した — 変えた中で一番面白かったのは？」',
          },
        },
        {
          time: '2:10',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🖥️ Teacher Demo — 2 Robot Dogs Walking', ja: '🖥️ 先生デモ — 2匹のロボット犬が歩く' },
          type: 'lesson',
          emoji: '🐕',
          content: {
            en: 'Teacher runs the robot dog project. Show:\n• How 2 robots are loaded at different positions\n• How to control them independently\n• What happens when you change speed or direction\n\nAsk: "Can you make them walk toward each other? Away from each other?"',
            ja: '先生がロボット犬プロジェクトを実行。見せる：\n• 2つのロボットを異なる位置に読み込む方法\n• 独立して制御する方法\n• 速度や方向を変えたらどうなるか\n\n質問：「互いに向かって歩かせることができる？反対方向に？」',
          },
        },
        {
          time: '2:25',
          duration: { en: '60 min', ja: '60分' },
          title: { en: '💻 Project 2 + Free Exploration', ja: '💻 プロジェクト2＋自由探索' },
          type: 'activity',
          emoji: '🚀',
          content: {
            en: 'Part 1 (30 min): Pick the project you DIDN\'T do yesterday:\n🤏 Gripper (if you did arm yesterday)\n💪 Robot Arm (if you did gripper yesterday)\n🐕 Robot Dogs (brand new — try if you want!)\n\nPart 2 (30 min): Free challenge:\n• Can you make the gripper open faster than it closes?\n• Can you make the arm go to 3 different positions in sequence?\n• Can you make 2 robot dogs do different things at the same time?\n• Write your OWN function: def myRobotMove():',
            ja: 'パート1（30分）：昨日やらなかったプロジェクトを選ぶ：\n🤏 グリッパー（昨日アームをやった場合）\n💪 ロボットアーム（昨日グリッパーをやった場合）\n🐕 ロボット犬（全く新しい！）\n\nパート2（30分）：自由チャレンジ：\n• グリッパーを閉じるより速く開けられる？\n• アームを3つの位置に順番に動かせる？\n• 自分だけの関数を書く：def myRobotMove():',
          },
        },
        {
          time: '3:25',
          duration: { en: '20 min', ja: '20分' },
          title: { en: '🏆 Final Showcase — Best Robot Program of the Week!', ja: '🏆 最終発表 — 今週のベストロボットプログラム！' },
          type: 'share',
          emoji: '🏆',
          content: {
            en: '3–4 students show their best robot program from this week:\n"I started with the starter code. I changed ___, ___, and ___. Watch what happens!"\n\nClass votes: Most Creative · Most Dramatic Movement · Best Function Name',
            ja: '3〜4人の生徒が今週のベストロボットプログラムをシェア：\n「スターターコードから始めた。___、___、___を変えた。実行したら何が起こるか見て！」\n\nクラスが投票：最もクリエイティブ · 最もダイナミックな動き · ベスト関数名',
          },
        },
        {
          time: '3:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: '🌟 Wrap-Up — You Are a Robot Programmer! 🤖', ja: '🌟 まとめ — あなたはロボットプログラマー！🤖' },
          type: 'stamp',
          emoji: '🏅',
          content: {
            en: '"This week I learned ___. My favorite project was ___ because ___. If I had one more day I would ___!"\n\nTeacher: "This week you drew art with Python AND controlled real robots. You are coders!"\n\n⭐ Stamp criteria: Completed Project 2 AND showed the class their robot running.',
            ja: '「今週___を学んだ。一番好きなプロジェクトは___、なぜなら___。もう1日あったら___！」\n\n先生：「今週はPythonでアートを描いて、本物のロボットを制御した。あなたたちはコーダーだ！」\n\n⭐ スタンプ条件：プロジェクト2を完了し、クラスにロボットを見せた。',
          },
        },
      ],
    },
    worksheets: [],
  },
];
